<template>
	<div class="afv2-builder">

		<template v-for="(group, gi) in local.groups">

			<!-- Connettore logico tra gruppi -->
			<v-row v-if="gi > 0" class="ma-0 my-1" :key="'glogic_' + gi">
				<v-col cols="12" class="pa-0 text-center">
					<v-btn-toggle
						:value="local.logic"
						@change="setGroupsLogic"
						mandatory
						dense
						rounded
						color="primary">
						<v-btn x-small value="and">E</v-btn>
						<v-btn x-small value="or">Oppure</v-btn>
					</v-btn-toggle>
				</v-col>
			</v-row>

			<v-card :key="'group_' + gi" outlined class="mb-2 afv2-group">
				<v-card-text class="pa-2">

					<div class="d-flex align-center mb-2">
						<v-btn-toggle
							:value="group.logic"
							@change="(val) => setGroupLogic(gi, val)"
							mandatory
							dense
							rounded
							color="primary">
							<v-btn x-small value="and">Tutte</v-btn>
							<v-btn x-small value="or">Almeno una</v-btn>
						</v-btn-toggle>
						<v-spacer></v-spacer>
						<v-btn v-if="local.groups.length > 1" icon x-small @click="removeGroup(gi)">
							<v-icon small color="error">mdi-delete-outline</v-icon>
						</v-btn>
					</div>

					<div v-for="(condition, ci) in group.conditions" :key="'cond_' + gi + '_' + ci" class="afv2-condition mb-2">

						<div class="d-flex align-center">
							<v-autocomplete
								:items="fieldItems"
								:value="condition.field"
								@change="(val) => setField(gi, ci, val)"
								label="Campo"
								item-text="label"
								item-value="key"
								:menu-props="{'offset-y': true}"
								outlined
								dense
								hide-details
								auto-select-first
								class="afv2-field-select"
							></v-autocomplete>
							<v-btn icon x-small class="ml-1" @click="removeCondition(gi, ci)">
								<v-icon small>mdi-close</v-icon>
							</v-btn>
						</div>

						<template v-if="condition.field">
							<v-select
								:items="operatorItems(condition)"
								:value="condition.operator"
								@change="(val) => setOperator(gi, ci, val)"
								label="Operatore"
								:menu-props="{'offset-y': true}"
								outlined
								dense
								hide-details
								class="mt-2"
							></v-select>

							<!-- Valore: nessuno -->
							<template v-if="isNoValue(condition)"></template>

							<!-- Valore: N giorni -->
							<v-text-field
								v-else-if="isNDays(condition)"
								type="number"
								label="Giorni"
								:value="condition.value"
								@input="(val) => setValue(gi, ci, val ? parseInt(val) : null)"
								outlined
								dense
								hide-details
								class="mt-2"
							></v-text-field>

							<!-- Valore: intervallo numerico -->
							<div v-else-if="isRange(condition) && conditionType(condition) === 'number'" class="d-flex mt-2">
								<v-text-field
									type="number"
									label="Da"
									:value="rangeValue(condition).from"
									@input="(val) => setRange(gi, ci, 'from', val)"
									outlined
									dense
									hide-details
									class="mr-1"
								></v-text-field>
								<v-text-field
									type="number"
									label="A"
									:value="rangeValue(condition).to"
									@input="(val) => setRange(gi, ci, 'to', val)"
									outlined
									dense
									hide-details
									class="ml-1"
								></v-text-field>
							</div>

							<!-- Valore: intervallo date -->
							<div v-else-if="isRange(condition) && conditionType(condition) === 'date'" class="mt-2">
								<gmh-date-picker
									label="Dal"
									:type="datePickerType(condition)"
									:value="datePickerValue(rangeValue(condition).from, condition)"
									@input="(val) => setRange(gi, ci, 'from', val)"
									:clear-value="null"
									:nudge-right="0"
									hide-details
									field-class="mb-2"
								></gmh-date-picker>
								<gmh-date-picker
									label="Al"
									:type="datePickerType(condition)"
									:value="datePickerValue(rangeValue(condition).to, condition)"
									@input="(val) => setRange(gi, ci, 'to', val)"
									:clear-value="null"
									:nudge-right="0"
									hide-details
								></gmh-date-picker>
							</div>

							<!-- Valore: fascia oraria -->
							<div v-else-if="isRange(condition) && conditionType(condition) === 'time'" class="d-flex mt-2">
								<v-text-field
									type="time"
									label="Dalle"
									:value="rangeValue(condition).from"
									@input="(val) => setRange(gi, ci, 'from', val)"
									outlined
									dense
									hide-details
									class="mr-1"
								></v-text-field>
								<v-text-field
									type="time"
									label="Alle"
									:value="rangeValue(condition).to"
									@input="(val) => setRange(gi, ci, 'to', val)"
									outlined
									dense
									hide-details
									class="ml-1"
								></v-text-field>
							</div>

							<!-- Valore: orario singolo -->
							<v-text-field
								v-else-if="conditionType(condition) === 'time'"
								type="time"
								label="Orario"
								:value="condition.value"
								@input="(val) => setValue(gi, ci, val || null)"
								outlined
								dense
								hide-details
								class="mt-2"
							></v-text-field>

							<!-- Valore: data singola -->
							<gmh-date-picker
								v-else-if="isSingleDate(condition)"
								:label="datePickerType(condition) === 'datetime' ? 'Data e ora' : 'Data'"
								:type="datePickerType(condition)"
								:value="datePickerValue(condition.value, condition)"
								@input="(val) => setValue(gi, ci, val)"
								:clear-value="null"
								:nudge-right="0"
								hide-details
								field-class="mt-2"
							></gmh-date-picker>

							<!-- Valore: multiplo su opzioni (relazioni, generalTypes, chips).
								 La key include il campo: cambiandolo il componente si ricrea e
								 svuota la cache-items dei campi async, che altrimenti mescola le
								 opzioni del campo precedente (es. aziende cercate prima dei prodotti) -->
							<v-autocomplete
								v-else-if="isOptions(condition)"
								:key="'val_' + gi + '_' + ci + '_' + condition.field"
								:items="optionsFor(condition, gi)"
								:value="condition.value"
								@input="(val) => setValue(gi, ci, val)"
								label="Valori"
								multiple
								small-chips
								deletable-chips
								clearable
								:menu-props="{'offset-y': true}"
								outlined
								dense
								hide-details
								class="mt-2"
								:return-object="!!fieldDef(condition.field).async"
								:search-input.sync="search[gi + '_' + ci]"
								:cache-items="!!fieldDef(condition.field).async"
								:filter="(item, queryText, itemText) => $eventBusUtility.customFilter(item, queryText, itemText, fieldDef(condition.field).searchKeys)"
							>
								<template v-slot:item="data">
									<template v-if="data.item == null || typeof data.item !== 'object'">
										<v-list-item-content v-text="data.item"></v-list-item-content>
									</template>
									<template v-else>
										<v-list-item-avatar v-if="getIcon(data.item)"
											size="32px"
											style="min-width: 32px; width: 32px; height: 32px; border-radius: 50%"
											class="primary mr-4">
											<v-icon class="fs18i" color="white">{{ getIcon(data.item) }}</v-icon>
										</v-list-item-avatar>
										<v-list-item-content>
											<v-list-item-title>{{ data.item.text }}</v-list-item-title>
											<v-list-item-subtitle v-if="subtitleKeys(condition).length">
												<span v-for="(key, k) in subtitleKeys(condition)" :key="k" :class="getTextClass(key)">{{ $trans(data.item[key]) }}&nbsp;</span>
											</v-list-item-subtitle>
										</v-list-item-content>
									</template>
								</template>
								<template v-slot:no-data>
									<v-list-item>
										<v-list-item-content>
											<v-list-item-title v-if="fieldDef(condition.field).async">Cerca...</v-list-item-title>
											<v-list-item-title v-else>Nessun risultato</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</template>
							</v-autocomplete>

							<!-- Valore: lista di testi liberi -->
							<v-combobox
								v-else-if="isFreeMulti(condition)"
								:value="condition.value"
								@input="(val) => setValue(gi, ci, val)"
								label="Valori (premi invio per aggiungere)"
								multiple
								small-chips
								deletable-chips
								clearable
								outlined
								dense
								hide-details
								class="mt-2"
							></v-combobox>

							<!-- Valore: numero singolo -->
							<v-text-field
								v-else-if="conditionType(condition) === 'number'"
								type="number"
								label="Valore"
								:value="condition.value"
								@input="(val) => setValue(gi, ci, val === '' ? null : Number(val))"
								outlined
								dense
								hide-details
								class="mt-2"
							></v-text-field>

							<!-- Valore: testo -->
							<v-text-field
								v-else
								label="Valore"
								:value="condition.value"
								@input="(val) => setValue(gi, ci, val)"
								outlined
								dense
								hide-details
								class="mt-2"
							></v-text-field>
						</template>
					</div>

					<v-btn text x-small color="primary" @click="addCondition(gi)">
						<v-icon left small>mdi-plus</v-icon>
						Aggiungi condizione
					</v-btn>
				</v-card-text>
			</v-card>
		</template>

		<v-btn text x-small color="primary" class="mb-2" @click="addGroup">
			<v-icon left small>mdi-plus-box-multiple-outline</v-icon>
			Aggiungi gruppo
		</v-btn>

		<div v-if="showRelationHint" class="caption grey--text text--darken-1">
			Le condizioni sulla stessa relazione nello stesso gruppo valgono sulla stessa riga (es. prodotto e quantità). Lo stesso campo ripetuto vale invece su righe diverse: per un intervallo usa «tra». Per righe diverse usa gruppi separati.
		</div>

	</div>
</template>

<script>
import Vue from 'vue'
import InputMixin from '@/mixins/InputMixin'

// Cache condivisa dei campi filtrabili dei correlati (drill-down): per le n-n
// chiave modelDef|relazione (campi della riga), per le altre il modello
const relatedFieldsCache = {}

import {
	NO_VALUE_OPERATORS,
	N_DAYS_OPERATORS,
	RANGE_OPERATORS,
	MULTI_VALUE_OPERATORS,
	operatorsFor,
	defaultOperator,
	builderType,
	emptyCondition,
	emptyGroup,
	emptyPayload,
	normalizeV2,
	relatedFieldsUrl,
	relatedFieldsCacheKey,
	hasManyToManyConditions,
	drillFieldItems
} from '@/utils/filtersV2'

export default {
	name: 'advanced-filters-builder-v2',
	mixins: [InputMixin],
	props: {
		value: Object,
		availableFilters: Array,
		modelDef: String
	},

	data() {
		return {
			local: this.value ? normalizeV2(this.value) : emptyPayload(),
			search: {},
			relatedFields: {}
		}
	},

	computed: {
		// Relazioni drillabili derivate dai filtri relazione del modello
		// (filterset con relation stringa + relation_model). Il filterset
		// dichiara la relazione sul proprio modello, che può non esistere su
		// quello filtrato: il backend lo verifica e lo dice con drillable
		// (assente sui metadati vecchi, quindi vale la regola storica)
		drillableRelations() {
			return _.sortBy((this.availableFilters || []).filter(f =>
				['belongsTo', 'belongsToMany'].includes(f.type) &&
				typeof f.relation === 'string' && f.relation && f.relation_model &&
				f.drillable !== false
			), 'label')
		},

		// Hint sulla regola della stessa riga: visibile solo se il payload ha
		// condizioni su relazioni n-n (filtro relazione o drill R.* con R n-n)
		showRelationHint() {
			return hasManyToManyConditions(this.local, this.availableFilters)
		},

		fieldItems() {
			const items = _.sortBy(this.availableFilters || [], 'label')

			_.forEach(this.drillableRelations, (rel) => {
				const fields = this.relatedFields[rel.relation]
				if (!fields || !fields.length) return
				items.push(...drillFieldItems(rel, fields))
			})

			return items
		}
	},

	watch: {
		value: {
			handler(val) {
				const incoming = val ? normalizeV2(val) : emptyPayload()
				if (JSON.stringify(incoming) !== JSON.stringify(this.local)) {
					this.local = incoming
				}
			},
			deep: true
		},

		search: {
			handler() {
				_.forEach(this.search, (text, key) => {
					const [gi, ci] = key.split('_')
					const condition = this.local.groups[gi] && this.local.groups[gi].conditions[ci]
					if (!condition || !condition.field) return
					const def = this.fieldDef(condition.field)
					if (def && def.async) {
						this.$eventBusUtility.asyncSearch(def, text)
					}
				})
			},
			deep: true
		},

		// Se gli availableFilters arrivano/cambiano dopo il mount,
		// ricarica le relazioni drillabili
		availableFilters() {
			this.loadRelatedFields()
		}
	},

	methods: {
		emit() {
			this.$emit('input', JSON.parse(JSON.stringify(this.local)))
		},

		fieldDef(key) {
			const main = _.find(this.availableFilters, (f) => f.key === key)
			if (main) return main
			if (key && key.indexOf('.') !== -1) {
				// split solo sul primo punto: il sotto-campo può contenerne altri
				const idx = key.indexOf('.')
				const relation = key.slice(0, idx)
				const subKey = key.slice(idx + 1)
				const fields = this.relatedFields[relation] || []
				const def = _.find(fields, (f) => f.key === subKey)
				if (def) return def
			}
			return {}
		},

		// Carica i campi filtrabili dei correlati: la cache condivisa contiene
		// la Promise, così richieste concorrenti sullo stesso modello e i
		// fallimenti non generano chiamate duplicate
		loadRelatedFields() {
			_.forEach(this.drillableRelations, (rel) => {
				// per le n-n i campi della riga dipendono dal modello padre
				const cacheKey = relatedFieldsCacheKey(rel, this.modelDef)
				if (!relatedFieldsCache[cacheKey]) {
					relatedFieldsCache[cacheKey] = this.axios.get(relatedFieldsUrl(rel, this.modelDef))
						.then(response => response.data.data.fields || [])
						.catch(() => [])
				}
				relatedFieldsCache[cacheKey].then(fields => {
					Vue.set(this.relatedFields, rel.relation, fields)
				})
			})
		},

		conditionType(condition) {
			return builderType(this.fieldDef(condition.field))
		},

		operatorItems(condition) {
			return operatorsFor(this.fieldDef(condition.field))
		},

		isNoValue(condition) {
			return NO_VALUE_OPERATORS.includes(condition.operator)
		},

		isNDays(condition) {
			return N_DAYS_OPERATORS.includes(condition.operator)
		},

		isRange(condition) {
			return RANGE_OPERATORS.includes(condition.operator)
		},

		isSingleDate(condition) {
			return this.conditionType(condition) === 'date' && ['on', 'before', 'after'].includes(condition.operator)
		},

		isDatetime(condition) {
			const def = this.fieldDef(condition.field)
			return !!def && def.type === 'datetime'
		},

		// Sui campi datetime prima/dopo/compreso tra chiedono anche l'ora;
		// "il giorno" resta sulla giornata intera
		datePickerType(condition) {
			return this.isDatetime(condition) && condition.operator !== 'on' ? 'datetime' : 'date'
		},

		// Un valore con orario rimasto da "prima del" non va mostrato nel
		// calendario semplice di "il giorno"
		datePickerValue(value, condition) {
			return value && this.datePickerType(condition) === 'date' ? String(value).slice(0, 10) : value
		},

		isOptions(condition) {
			const type = this.conditionType(condition)
			return ['relation', 'chips'].includes(type) && MULTI_VALUE_OPERATORS.includes(condition.operator)
		},

		// Multi-valore a testo libero (es. "è uno di" / "contiene uno di" sui campi testuali)
		isFreeMulti(condition) {
			return MULTI_VALUE_OPERATORS.includes(condition.operator) && !this.isOptions(condition)
		},

		optionsFor(condition, gi) {
			const def = this.fieldDef(condition.field)
			const options = def.options || []
			if (!def.depends_filter_key || gi === undefined) return options
			const optionKey = def.depends_option_key || def.depends_filter_key
			// Cascata solo se le opzioni espongono davvero la chiave di dipendenza
			// (es. backend non ancora aggiornato): altrimenti lista completa
			if (!options.length || !options[0] || typeof options[0] !== 'object' || !(optionKey in options[0])) return options
			const parentIds = this.dependsValues(gi, def.depends_filter_key)
			if (!parentIds.length) return options
			return options.filter((o) => parentIds.includes(String(o[optionKey])))
		},

		// Valori selezionati nelle condizioni "padre" dello stesso gruppo
		// (solo operatore "è uno di"): guidano la cascata dei campi dipendenti
		dependsValues(gi, parentKey) {
			const group = this.local.groups[gi] || {}
			return _.uniq(_.flatMap(group.conditions || [], (c) => {
				if (c.field !== parentKey || c.operator !== 'in' || !Array.isArray(c.value)) return []
				return c.value.map((v) => String(v && typeof v === 'object' ? v.value : v))
			}))
		},

		// Chiavi da mostrare come sottotitolo delle opzioni (es. indirizzo
		// delle destinazioni): searchKeys del campo o subtitle delle opzioni
		subtitleKeys(condition) {
			const def = this.fieldDef(condition.field)
			if (def.searchKeys && def.searchKeys.length) return _.without(def.searchKeys, 'barred')
			const options = def.options || []
			return options.length && options[0] && options[0].subtitle ? ['subtitle'] : []
		},

		// Rimuove dai campi dipendenti del gruppo i valori non più ammessi
		// dopo una modifica alla condizione padre (solo interazione utente)
		pruneDependents(gi, parentKey) {
			if (!parentKey) return
			_.forEach(this.local.groups[gi].conditions, (c) => {
				const def = this.fieldDef(c.field)
				if (def.depends_filter_key !== parentKey || !Array.isArray(c.value)) return
				const allowed = this.optionsFor(c, gi).map((o) => String(o && typeof o === 'object' ? o.value : o))
				const pruned = c.value.filter((v) => allowed.includes(String(v && typeof v === 'object' ? v.value : v)))
				if (pruned.length !== c.value.length) Vue.set(c, 'value', pruned)
			})
		},

		rangeValue(condition) {
			return condition.value && typeof condition.value === 'object' ? condition.value : {}
		},

		setGroupsLogic(val) {
			this.local.logic = val === 'or' ? 'or' : 'and'
			this.emit()
		},

		setGroupLogic(gi, val) {
			Vue.set(this.local.groups[gi], 'logic', val === 'or' ? 'or' : 'and')
			this.emit()
		},

		setField(gi, ci, key) {
			const condition = this.local.groups[gi].conditions[ci]
			Vue.set(condition, 'field', key)
			Vue.set(condition, 'operator', defaultOperator(this.fieldDef(key)))
			Vue.set(condition, 'value', null)
			// Il testo cercato apparteneva al campo precedente
			Vue.delete(this.search, gi + '_' + ci)
			this.emit()
		},

		setOperator(gi, ci, operator) {
			const condition = this.local.groups[gi].conditions[ci]
			const wasMulti = MULTI_VALUE_OPERATORS.includes(condition.operator)
			const isMulti = MULTI_VALUE_OPERATORS.includes(operator)
			Vue.set(condition, 'operator', operator)
			if (NO_VALUE_OPERATORS.includes(operator) || wasMulti !== isMulti ||
				RANGE_OPERATORS.includes(operator) || N_DAYS_OPERATORS.includes(operator)) {
				Vue.set(condition, 'value', RANGE_OPERATORS.includes(operator) ? {} : null)
			}
			this.pruneDependents(gi, condition.field)
			this.emit()
		},

		setValue(gi, ci, val) {
			const condition = this.local.groups[gi].conditions[ci]
			Vue.set(condition, 'value', val)
			this.pruneDependents(gi, condition.field)
			this.emit()
		},

		setRange(gi, ci, side, val) {
			const condition = this.local.groups[gi].conditions[ci]
			const current = condition.value && typeof condition.value === 'object' ? condition.value : {}
			Vue.set(condition, 'value', Object.assign({}, current, { [side]: val === '' ? null : val }))
			this.emit()
		},

		addCondition(gi) {
			this.local.groups[gi].conditions.push(emptyCondition())
			this.emit()
		},

		removeCondition(gi, ci) {
			this.local.groups[gi].conditions.splice(ci, 1)
			if (!this.local.groups[gi].conditions.length) {
				if (this.local.groups.length > 1) {
					this.local.groups.splice(gi, 1)
				} else {
					this.local.groups[gi].conditions.push(emptyCondition())
				}
			}
			this.emit()
		},

		addGroup() {
			this.local.groups.push(emptyGroup())
			this.emit()
		},

		removeGroup(gi) {
			this.local.groups.splice(gi, 1)
			if (!this.local.groups.length) {
				this.local.groups.push(emptyGroup())
			}
			this.emit()
		}
	},

	created() {
		this.loadRelatedFields()
	}
}
</script>

<style scoped>
.afv2-group {
	border-radius: 8px;
}
.afv2-condition {
	border-left: 2px solid var(--v-primary-base);
	padding-left: 8px;
}
.afv2-field-select {
	flex: 1;
}
</style>
